// File: frontend/src/pages/AdminKnowledgePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { NavLink } from 'react-router-dom'
import axios from 'axios'
import {
  FiInbox,
  FiUploadCloud,
  FiTrash2,
  FiAlertTriangle,
  FiLoader,
  FiDatabase,
  FiInfo,
  FiFileText,
  FiRefreshCw,
  FiBarChart2,
  FiMenu,
  FiMessageSquare,
  FiLogOut,
  FiX,
  FiShield,
  FiAward,
} from 'react-icons/fi'
import './AdminKnowledgePage.css' // FIX: Added missing CSS import

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

// Sidebar component - Unified and consistent with the dashboard
const AdminSidebar = ({ user, logout }) => (
  <>
    <nav className="admin-sidebar-nav">
      <NavLink to="/admin/dashboard" className="admin-nav-link">
        <FiBarChart2 size={20} /> Dashboard
      </NavLink>
      <NavLink to="/admin/kb" className="admin-nav-link">
        <FiDatabase size={20} /> Knowledge
      </NavLink>
      {/* Features link - Only show to superadmins */}
      {user?.roles?.includes(
        process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
      ) && (
        <NavLink to="/admin/features" className="admin-nav-link">
          <FiAward size={20} /> Features
        </NavLink>
      )}
      <NavLink to="/admin/privacy" className="admin-nav-link">
        <FiShield size={20} /> Privacy
      </NavLink>
      <NavLink to="/admin/feedback" className="admin-nav-link">
        <FiInbox size={20} /> Feedback
      </NavLink>
      <NavLink to="/chat" className="admin-nav-link">
        <FiMessageSquare size={20} /> Chat
      </NavLink>
    </nav>
    <div className="admin-sidebar-footer">
      <div className="admin-user-info">
        {user && (
          <p className="admin-user-display-name" title={user.display_name}>
            {user.display_name || user.first_name || 'Utente'}
          </p>
        )}
      </div>
      <div className="admin-sidebar-footer-actions">
        <button onClick={logout} className="admin-icon-button small" title="Esci">
          <FiLogOut />
        </button>
      </div>
    </div>
  </>
)

const AdminKnowledgePage = () => {
  const { token, user, logout } = useAuthContext() // Added logout
  const [selectedFile, setSelectedFile] = useState(null)
  const [ingestionStatus, setIngestionStatus] = useState('')
  const [ingestionLog, setIngestionLog] = useState([])
  const [isIngesting, setIsIngesting] = useState(false)
  const [ingestionError, setIngestionError] = useState('')
  const [sourceFiles, setSourceFiles] = useState([])
  const [isLoadingSources, setIsLoadingSources] = useState(false)
  const [sourcesError, setSourcesError] = useState('')
  const [showDeleteSourceConfirm, setShowDeleteSourceConfirm] = useState(false)
  const [sourceToDelete, setSourceToDelete] = useState(null)
  const [isDeletingSource, setIsDeletingSource] = useState(false)
  const [deleteSourceStatus, setDeleteSourceStatus] = useState('')
  const [deleteSourceError, setDeleteSourceError] = useState('')
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false)
  const [isDeletingAll, setIsDeletingAll] = useState(false)
  const [deleteAllStatus, setDeleteAllStatus] = useState('')
  const [deleteAllError, setDeleteAllError] = useState('')
  const [kbStats, setKbStats] = useState(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [statsError, setStatsError] = useState('')
  const [isSidebarOpen, setSidebarOpen] = useState(false)

  const fileInputRef = useRef(null)

  const isAdmin = user?.roles?.includes(
    process.env.REACT_APP_ADMIN_ROLE_NAME || 'admin'
  )

  const fetchKbStats = useCallback(async () => {
    if (!token || !isAdmin) return
    setIsLoadingStats(true)
    setStatsError('')
    try {
      const response = await axios.get(`${API_BASE_URL}/knowledge/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setKbStats(response.data.success ? response.data.stats : null)
      if (!response.data.success) {
        setStatsError(response.data.message || 'Impossibile caricare le statistiche.')
      }
    } catch (err) {
      setStatsError(
        err.response?.data?.detail || 'Errore nel recupero delle statistiche KB.'
      )
    } finally {
      setIsLoadingStats(false)
    }
  }, [token, isAdmin])

  const fetchSourceFiles = useCallback(async () => {
    if (!token || !isAdmin) return
    setIsLoadingSources(true)
    setSourcesError('')
    try {
      const response = await axios.get(`${API_BASE_URL}/knowledge/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setSourceFiles(response.data)
    } catch (err) {
      setSourcesError(
        err.response?.data?.detail || 'Errore nel caricamento dei file sorgente.'
      )
    } finally {
      setIsLoadingSources(false)
    }
  }, [token, isAdmin])

  useEffect(() => {
    if (isAdmin) {
      fetchKbStats()
      fetchSourceFiles()
    }
  }, [fetchKbStats, fetchSourceFiles, isAdmin])

  const handleFileChange = event => {
    setSelectedFile(event.target.files[0])
    setIngestionStatus('')
    setIngestionLog([])
    setIngestionError('')
  }

  const handleIngestFile = async () => {
    if (!selectedFile || !token) return
    setIsIngesting(true)
    setIngestionStatus('Avvio importazione...')
    setIngestionLog([])
    setIngestionError('')
    const formData = new FormData()
    formData.append('file', selectedFile)
    try {
      const response = await axios.post(
        `${API_BASE_URL}/knowledge/ingest-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: progressEvent => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setIngestionStatus(`Caricamento ${selectedFile.name}: ${percentCompleted}%`)
          },
        }
      )
      setIngestionStatus(response.data.message || 'Processo completato.')
      setIngestionLog(response.data.status_log || [])
      if (response.data.success) {
        fetchKbStats()
        fetchSourceFiles()
      } else {
        setIngestionError(response.data.message || 'Importazione fallita.')
      }
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Errore imprevisto durante l'importazione."
      setIngestionError(errorDetail)
      setIngestionStatus(`Fallito: ${errorDetail}`)
      setIngestionLog(err.response?.data?.status_log || [])
    } finally {
      setIsIngesting(false)
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = null
      }
    }
  }

  const handleDeleteBySource = async () => {
    if (!sourceToDelete || !token) return
    setIsDeletingSource(true)
    setDeleteSourceStatus(`Eliminazione di ${sourceToDelete}...`)
    setDeleteSourceError('')
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/knowledge/clear-by-source?source_filename=${encodeURIComponent(
          sourceToDelete
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setDeleteSourceStatus(response.data.message || 'Richiesta elaborata.')
      if (response.data.success) {
        fetchKbStats()
        fetchSourceFiles()
      } else {
        setDeleteSourceError(response.data.message || 'Impossibile eliminare.')
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "Errore durante l'eliminazione."
      setDeleteSourceError(errorDetail)
      setDeleteSourceStatus(`Fallito: ${errorDetail}`)
    } finally {
      setIsDeletingSource(false)
      setShowDeleteSourceConfirm(false)
      setSourceToDelete(null)
    }
  }

  const handleDeleteAll = async () => {
    if (!token) return
    setIsDeletingAll(true)
    setDeleteAllStatus('Eliminazione di TUTTI i vettori...')
    setDeleteAllError('')
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/knowledge/clear-all?confirm=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setDeleteAllStatus(response.data.message || 'Richiesta elaborata.')
      if (response.data.success) {
        fetchKbStats()
        fetchSourceFiles()
      } else {
        setDeleteAllError(response.data.message || 'Impossibile eliminare.')
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "Errore durante l'eliminazione."
      setDeleteAllError(errorDetail)
      setDeleteAllStatus(`Fallito: ${errorDetail}`)
    } finally {
      setIsDeletingAll(false)
      setShowDeleteAllConfirm(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="admin-page-container">
        <main
          className="admin-main-content"
          style={{ margin: 'auto', maxWidth: '600px' }}
        >
          <div className="admin-card" style={{ textAlign: 'center' }}>
            <FiAlertTriangle size={48} className="danger" />
            <h2>Accesso Negato</h2>
            <p>Non hai i permessi per visualizzare questa pagina.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      {isSidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <img
            src="/franca-logo.png"
            alt="F.R.A.N.C.A."
            className="admin-sidebar-logo"
          />
          <button
            className="mobile-sidebar-close-button"
            onClick={() => setSidebarOpen(false)}
          >
            <FiX size={24} />
          </button>
        </div>
        <AdminSidebar user={user} logout={logout} />
      </aside>

      <main className="admin-main-content">
        <header className="admin-page-header">
          <button
            className="admin-button mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <h1>
            <FiDatabase /> Gestione Knowledge Base
          </h1>
        </header>

        <section className="admin-card">
          <div className="admin-card-header">
            <h2>
              <FiInfo /> Statistiche Indice
            </h2>
            <button
              onClick={fetchKbStats}
              disabled={isLoadingStats}
              className="admin-button secondary"
            >
              {isLoadingStats ? <FiLoader className="spinner-icon" /> : <FiRefreshCw />}{' '}
              Aggiorna
            </button>
          </div>
          {isLoadingStats && (
            <div className="admin-loading-state">
              <FiLoader className="spinner-icon" /> Caricamento...
            </div>
          )}
          {statsError && <div className="admin-error-message small">{statsError}</div>}
          {kbStats && !isLoadingStats && (
            <div className="admin-insight-grid">
              <div className="admin-insight-card">
                <p>Vettori Totali</p>
                <span>{(kbStats.total_vector_count ?? 0).toLocaleString('it-IT')}</span>
              </div>
              <div className="admin-insight-card">
                <p>Dimensione</p>
                <span>{kbStats.dimension ?? 'N/D'}</span>
              </div>
              <div className="admin-insight-card">
                <p>Riempimento Indice</p>
                <span>
                  {kbStats.index_fullness
                    ? `${(kbStats.index_fullness * 100).toFixed(2)}%`
                    : 'N/D'}
                </span>
              </div>
            </div>
          )}
        </section>

        <section className="admin-card">
          <h2>
            <FiUploadCloud /> Importa Nuovo Documento
          </h2>
          <div className="form-group">
            <input
              type="file"
              id="kb-file-input"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.txt,.md,.docx,.csv"
            />
            {selectedFile && (
              <p className="file-selected-info">Selezionato: {selectedFile.name}</p>
            )}
          </div>
          <button
            onClick={handleIngestFile}
            disabled={!selectedFile || isIngesting}
            className="admin-button primary"
          >
            {isIngesting ? <FiLoader className="spinner-icon" /> : <FiUploadCloud />}
            {isIngesting ? 'Importazione...' : 'Importa File'}
          </button>
          {ingestionStatus && (
            <p className={`status-message ${ingestionError ? 'error' : 'info'}`}>
              {ingestionStatus}
            </p>
          )}

          {ingestionLog.length > 0 && (
            <div className="ingestion-log">
              <h4>Log Dettagliato Importazione:</h4>
              <ul>
                {ingestionLog.map((log, index) => (
                  <li key={index}>{log}</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="admin-card">
          <div className="admin-card-header">
            <h2>
              <FiFileText /> Documenti Importati
            </h2>
            <button
              onClick={fetchSourceFiles}
              disabled={isLoadingSources}
              className="admin-button secondary"
            >
              {isLoadingSources ? (
                <FiLoader className="spinner-icon" />
              ) : (
                <FiRefreshCw />
              )}{' '}
              Aggiorna Elenco
            </button>
          </div>

          {deleteSourceStatus && (
            <p className={`status-message ${deleteSourceError ? 'error' : 'info'}`}>
              {deleteSourceStatus}
            </p>
          )}
          {isLoadingSources && (
            <div className="admin-loading-state">
              <FiLoader className="spinner-icon" /> Caricamento...
            </div>
          )}
          {sourcesError && (
            <div className="admin-error-message small">{sourcesError}</div>
          )}
          {!isLoadingSources && sourceFiles.length > 0 && (
            <ul className="source-list">
              {sourceFiles.map(filename => (
                <li key={filename} className="source-list-item">
                  <span className="file-info">
                    <FiFileText /> {filename}
                  </span>
                  <button
                    onClick={() => {
                      setSourceToDelete(filename)
                      setShowDeleteSourceConfirm(true)
                    }}
                    className="admin-button danger outline"
                    disabled={isDeletingSource}
                  >
                    <FiTrash2 />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!isLoadingSources && sourceFiles.length === 0 && !sourcesError && (
            <p>Nessun documento sorgente trovato.</p>
          )}
        </section>

        <section className="admin-card danger-zone">
          <h3>
            <FiAlertTriangle /> Azioni Pericolose
          </h3>
          <p>
            Questa azione è altamente distruttiva e rimuoverà permanentemente tutti i
            dati dalla knowledge base.
          </p>
          <button
            onClick={() => setShowDeleteAllConfirm(true)}
            disabled={isDeletingAll}
            className="admin-button danger"
            style={{ marginTop: '1rem' }}
          >
            <FiTrash2 /> Elimina Tutti i Vettori...
          </button>
          {deleteAllStatus && (
            <p className={`status-message ${deleteAllError ? 'error' : 'info'}`}>
              {deleteAllStatus}
            </p>
          )}
        </section>
      </main>

      {(showDeleteSourceConfirm || showDeleteAllConfirm) && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <FiAlertTriangle size={48} className="danger" />
            <h3>
              {showDeleteAllConfirm
                ? 'Eliminare Tutti i Vettori?'
                : 'Conferma Eliminazione'}
            </h3>
            <p>
              {showDeleteAllConfirm ? (
                'Sei assolutamente sicuro di voler eliminare TUTTI i vettori? Questa azione non può essere annullata.'
              ) : (
                <>
                  Sei sicuro di voler eliminare tutti i vettori associati a{' '}
                  <strong>{sourceToDelete}</strong>? L'azione non può essere annullata.
                </>
              )}
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => {
                  setShowDeleteSourceConfirm(false)
                  setShowDeleteAllConfirm(false)
                }}
                className="admin-button secondary"
                disabled={isDeletingSource || isDeletingAll}
              >
                Annulla
              </button>
              <button
                onClick={showDeleteAllConfirm ? handleDeleteAll : handleDeleteBySource}
                className="admin-button danger"
                disabled={isDeletingSource || isDeletingAll}
              >
                {isDeletingSource || isDeletingAll ? (
                  <>
                    <FiLoader className="spinner-icon" /> Eliminazione...
                  </>
                ) : showDeleteAllConfirm ? (
                  'Sì, Elimina Tutto'
                ) : (
                  'Sì, Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminKnowledgePage
